import { usePlayer } from '@bpm-web-app/components/src/lib/player/player.context';
import { Album as DownloadAlbum } from '@bpm-web-app/download-api-sdk';
import { Category, MediaWithAlbum } from '@bpm-web-app/stream-api-sdk';
import { useCategories, useTrending } from '@bpm-web-app/swr-hooks';
import {
    DEFAULT_BROWSE_TRENDING_LIMIT,
    DEFAULT_DROPDOWN_CATEGORY,
    DEFAULT_PERIOD,
    downloadAlbumWithMediaToQueueItem,
    QueueItem,
    streamMediaWithAlbumToQueueItem,
    useHubSwitch,
} from '@bpm-web-app/utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LibraryTabsContext } from '../../../../utils/src/lib/library-tabs.context';
import Trending, { ITrendingItem } from './trending';

interface TrendingBrowseSupremeProps {
    isDataLoaded?: boolean;
    setIsDataLoaded?: (loaded: boolean) => void;
}

export function TrendingBrowseSupreme({ isDataLoaded, setIsDataLoaded }: TrendingBrowseSupremeProps) {
    const { setQueue } = usePlayer();
    const { isDownload } = useHubSwitch();

    const [activeCategory, setActiveCategory] = useState<string>(DEFAULT_DROPDOWN_CATEGORY.value);
    const { libraryProperty } = useContext(LibraryTabsContext);

    const { data, isLoading } = useTrending(isDownload ? 'download' : 'stream', {
        period: DEFAULT_PERIOD,
        limit: DEFAULT_BROWSE_TRENDING_LIMIT,
        category: activeCategory || undefined,
        library: libraryProperty,
    });

    const allCategories = useCategories(libraryProperty)?.categories || ([] as Category[]);

    const trending = useMemo(
        () =>
            data?.data.length
                ? data.data.map((item) => {
                    return {
                        artist: item.artist,
                        cover_url: isDownload ? (item as DownloadAlbum).cover_url : (item as MediaWithAlbum).album?.cover_url,
                        trending_status: (item as DownloadAlbum | MediaWithAlbum).trending_status_weekly,
                        id: item.id,
                        title: item.name,
                        artists: (item as DownloadAlbum | MediaWithAlbum).artists,
                        media_id: isDownload ? (item as DownloadAlbum).media[0].id : (item as MediaWithAlbum).id,
                        tracking_id: isDownload ? (item as DownloadAlbum).id : (item as MediaWithAlbum).album?.id
                    } as ITrendingItem;
                }) : [],
        [data?.data, isDownload]
    );

    const handlePlayTrack = useCallback(
        (index: number) => {
            let queueItems: QueueItem[];
            if (isDownload && data?.data) {
                queueItems = (data.data as DownloadAlbum[]).map((album) => downloadAlbumWithMediaToQueueItem(album));
            } else {
                queueItems = (data.data as MediaWithAlbum[]).map((media) => streamMediaWithAlbumToQueueItem(media));
            }

            setQueue(queueItems, index);
        },
        [data?.data, isDownload, setQueue]
    );

    useEffect(() => {
        setActiveCategory(DEFAULT_DROPDOWN_CATEGORY.value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [libraryProperty]);

    useEffect(() => {
        if (setIsDataLoaded) {
            if (isLoading === false) {
                setIsDataLoaded(true);
            } else {
                setIsDataLoaded(false);
            }
        }
    }, [isLoading, setIsDataLoaded]);

    const makeFirstTrendingItemAlwaysGreen = useMemo(() => {
        const newTrendingItem = [...trending];
        const firstItem = newTrendingItem[0];
        if (firstItem) {
            firstItem.trending_status = Math.abs(firstItem.trending_status);
        }
        return newTrendingItem;
    }, [trending]);

    const shouldDisplayGhostLoading = useMemo(() => isDataLoaded === false || isLoading, [isDataLoaded, isLoading]);

    return (
        <Trending
            trending={makeFirstTrendingItemAlwaysGreen}
            isLoading={shouldDisplayGhostLoading}
            onPlayTrack={handlePlayTrack}
            platform="supreme"
            dropdownData={allCategories?.map((category) => ({ label: category.name, value: category.slug }))}
            activeDropdownValue={activeCategory}
            onDropdownChange={setActiveCategory}
        />
    );
}

export default TrendingBrowseSupreme;
